import {
  DEFAULT_SETTINGS,
  CAPS_VERSION,
  MAX_REGIONS_HARD,
  maxClipsPerCollectionForTier,
  wallMinutesPerDayForTier,
  isGoldTier,
  maxCollectionsForTier,
  maxRegionsForTier,
  maxScreensForTier,
  maxViewsForTier,
} from "@gooner/shared";
import { track } from "../analytics";
import { usePublicSettings } from "./publicSettings";
import { useSession } from "./session";

/**
 * GoonLords Gold, client side: the tier gate and the conversion funnel's vocabulary.
 *
 * The gate combines the session user (goldUntil, createdAt) with the public settings
 * block through the SAME shared tier helpers the server refuses with — a pre-empt here
 * is best-effort UX (the answer arrives with the click, the sign-in-prompt precedent)
 * and the server's 409 is always the backstop. Client staleness therefore fails
 * PERMISSIVE, never stricter than the server.
 */

// One settings fetch per page load, shared by every gate (lib/publicSettings.ts). Its
// failure fallback is DEFAULT_SETTINGS — selling off, caps off — the permissive direction.

interface GoldGateState {
  /** The user holds Gold right now. */
  gold: boolean;
  goldUntil: number | null;
  sellingEnabled: boolean;
  capsEnabled: boolean;
  price7d: number;
  price30d: number;
  price90d: number;
  price365d: number;
  paymentUrl: string;
  maxScreens: number;
  maxViews: number;
  maxCollections: number;
  maxRegions: number;
  maxClipsPerCollection: number;
  /** Wall-minutes a UTC day; Infinity for Gold, while the caps are dark, and until the settings answer. */
  wallMinutesPerDay: number;
}

const PERMISSIVE: GoldGateState = {
  gold: false,
  goldUntil: null,
  sellingEnabled: false,
  capsEnabled: false,
  price7d: DEFAULT_SETTINGS.gold.price7d,
  price30d: DEFAULT_SETTINGS.gold.price30d,
  price90d: DEFAULT_SETTINGS.gold.price90d,
  price365d: DEFAULT_SETTINGS.gold.price365d,
  paymentUrl: "",
  maxScreens: Number.POSITIVE_INFINITY,
  maxViews: Number.POSITIVE_INFINITY,
  maxCollections: Number.POSITIVE_INFINITY,
  maxRegions: MAX_REGIONS_HARD,
  maxClipsPerCollection: Number.POSITIVE_INFINITY,
  wallMinutesPerDay: Number.POSITIVE_INFINITY,
};

/** The tier gate: ceilings + sell state for the current session. Permissive until the
 *  settings answer arrives — a flash of "allowed" that the server would refuse beats a
 *  flash of a paywall nobody is behind. */
export function useGoldGate(): GoldGateState {
  const user = useSession((s) => s.user);
  const settings = usePublicSettings();
  if (!settings) return PERMISSIVE;
  const g = settings.gold ?? DEFAULT_SETTINGS.gold;
  const args = {
    owner: user?.id ?? null,
    goldUntil: user?.goldUntil ?? null,
    capsEnabled: g.capsEnabled,
    now: Date.now(),
  };
  return {
    gold: isGoldTier(args),
    goldUntil: user?.goldUntil ?? null,
    sellingEnabled: g.enabled,
    capsEnabled: g.capsEnabled,
    price7d: g.price7d,
    price30d: g.price30d,
    price90d: g.price90d,
    price365d: g.price365d,
    paymentUrl: g.paymentUrl,
    maxScreens: maxScreensForTier(args),
    maxViews: maxViewsForTier(args),
    maxCollections: maxCollectionsForTier(args),
    maxRegions: maxRegionsForTier(args),
    maxClipsPerCollection: maxClipsPerCollectionForTier(args),
    wallMinutesPerDay: wallMinutesPerDayForTier(args, g.wallMinutesFree),
  };
}

// ---- the funnel's closed vocabularies ---------------------------------------------
//
// analytics.ts keeps the NAME union; these wrappers close the PROPS at compile time.
// `regions` vs `layout` is deliberate: regions = growing the count by hand (add /
// duplicate / draw), layout = picking a locked over-cap preset or template — the
// split answers whether people want big walls via presets or by hand-building.

// `premium_wall` is the members-only surface, not a cap: nothing to delete to make
// room, the same call succeeds once the caller is a member. Spelled in full because
// "wall" alone already means a multi-region layout in this vocabulary (see `regions`).
// `vault`: a Basic account at Basic's vault ceiling — the one vault refusal Premium
// lifts, since Premium's ceiling is higher (2026-09-09).
// `allowance`: the day's wall-minutes spent and the screen shrunk (2026-09-10) — the
// one gate that is a METER, not a count, and the only one that fires while watching.
// `build`: Build a Slut's scroll (2026-09-19) — the rounds and one wall of her are
// everyone's, the endless scroll is Premium's; the gate fires on the swipe after that wall.
// `private_room` (2026-09-19): a Goon Room nobody can find — the link is the invite —
// is Premium; anyone may open a public one.
export type GoldGate = "screens" | "views" | "collections" | "regions" | "layout" | "premium_wall" | "vault" | "allowance" | "private_room" | "build";
// `faps` opens the plain pitch from the FAPS dialog's stipend line — a door into
// Premium that is neither a gate nor the menu, and worth telling apart in the funnel.
// `faps_cap` is the crown's day cap being hit by a Basic account — the one moment the
// higher Premium cap is worth hearing about, and the door most worth measuring on its own.
export type GoldUpsellGate = GoldGate | "lapse" | "menu" | "faps" | "faps_cap";
export type GoldCtaSource = GoldGate | "menu" | "lapse_nudge" | "faps" | "faps_cap";

// `caps` is the edition of the Basic line in force (shared CAPS_VERSION): the line moved
// on 2026-09-10, and a funnel that cannot tell a hit on the old 3·5·10·9 from one on the
// new 2·3·3·8 cannot say whether moving it did anything.
export const trackGoldGate = (gate: GoldGate): void => track("gold_gate_hit", { gate, caps: CAPS_VERSION });
export const trackGoldUpsell = (gate: GoldUpsellGate): void =>
  track("gold_upsell_shown", { gate, caps: CAPS_VERSION });
// The Patreon panel's two steps carry the same source as the dialog they sit in, plus
// the two surfaces that are not a dialog: the profile card, and the thanks page the
// OAuth callback lands on (a Subscribe pressed THERE is the connect-first flow working
// as designed, and worth telling apart from one pressed at a gate). Without a source
// the funnel knew that eight people left for Patreon in a month and nothing about
// what sent them.
export type PatreonCtaSource = GoldCtaSource | "settings" | "thanks";
export type PatreonCtaStep = "subscribe" | "connect";
export const trackPatreonCta = (step: PatreonCtaStep, source: PatreonCtaSource): void =>
  track("patreon_cta_clicked", { step, source });

/*
 * A ROW IS NOT A PITCH. The ☰ menu's Premium row (and its lapsed / ending-soon variant)
 * used to report as gold_upsell_shown {gate: "menu" | "lapse"} — the same event the
 * dialog fires — so for a month the funnel's top step counted everyone who opened the
 * menu as having seen the upsell. 1,405 "impressions" against 103 real dialogs. The
 * row gets its own event now, and gold_upsell_shown means the dialog, full stop — the
 * same split the faps-cap toast already keeps (faps_cap_nudge_shown for the toast,
 * gold_upsell_shown only when the dialog opens).
 *
 * Deduped once per page load — the screen_code_shown minted-set precedent: a menu row
 * that fired on every render would be a heartbeat, and one television once sent 618
 * of those.
 */
export type GoldRow = "menu" | "lapse";
const shownOnce = new Set<GoldRow>();
export function trackGoldImpression(gate: GoldRow): void {
  if (shownOnce.has(gate)) return;
  shownOnce.add(gate);
  track("gold_row_shown", { gate });
}
